import { useState, useEffect, useMemo, useRef } from 'react';
import { ClientEvent, SyncState, UserEvent, RoomStateEvent } from '../constants/matrixEvents';
import { useMatrixClient } from '../context/MatrixContext';
import { generateInitialsAvatar } from '../utils/avatar';
import { useAvatar } from '../context/AvatarContext';
import logger from '../utils/logger';

/**
 * Transforms a Matrix SDK User object into a simplified contact format for the UI.
 * Initially uses initials, then fetches real avatar asynchronously.
 * @param {import('matrix-js-sdk').User} user - The Matrix User object.
 * @returns {{id: string, name: string, avatar: string} | null}
 */
const transformUserToContact = (user) => {
  if (!user) return null;

  // Prefer rawDisplayName (clean) over displayName (may have SDK-added Matrix ID suffix)
  // Fall back to displayName for backward compatibility, then userId as last resort
  const cleanName = user.rawDisplayName || user.displayName || user.userId;

  return {
    id: user.userId,
    name: cleanName,
    // Start with initials as placeholder - will be replaced by real avatar
    avatar: generateInitialsAvatar(cleanName),
  };
};

/**
 * A hook to get a list of all users known to the Matrix client.
 * @returns {{contactList: object[], isLoading: boolean}}
 */
export const useContactList = () => {
  const client = useMatrixClient();
  const { getUserAvatar, invalidateAvatar } = useAvatar();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [avatars, setAvatars] = useState({}); // userId -> avatar URL

  // Ref to avoid dependency cycles
  const avatarsRef = useRef({});

  // Keep ref in sync with state
  avatarsRef.current = avatars;

  // Create stable user identifier for avatar effect dependency
  // Only changes when users are added/removed, not on every sync
  const userIds = useMemo(() => {
    return users.map(u => u.userId).sort().join(',');
  }, [users]);

  useEffect(() => {
    if (!client) {
      setIsLoading(true);
      setUsers([]);
      return;
    }

    const updateUsers = () => {
      // Get all cached users from the SDK
      const allUsers = client.getUsers();
      
      // Also get all users from room memberships to include invited users
      // who may not be in the SDK's user cache yet
      const rooms = client.getRooms?.() || [];
      const roomMemberIds = new Set();
      
      rooms.forEach(room => {
        // Get members with all membership types: join, invite, leave, ban
        const joinedMembers = room.getMembers?.() || [];
        const invitedMembers = room.getMembersWithMembership?.('invite') || [];
        const allRoomMembers = [...joinedMembers, ...invitedMembers];
        
        allRoomMembers.forEach(member => {
          roomMemberIds.add(member.userId);
        });
      });
      
      // Create User objects for room members not in SDK cache
      const existingUserIds = new Set(allUsers.map(u => u.userId));
      const additionalUsers = [];
      
      roomMemberIds.forEach(userId => {
        if (!existingUserIds.has(userId)) {
          // Get or create User object for this member
          const user = client.getUser?.(userId);
          if (user) {
            additionalUsers.push(user);
          }
        }
      });
      
      // Combine SDK users with room member users
      const combinedUsers = [...allUsers, ...additionalUsers];
      setUsers(combinedUsers);
    };

    const handleSync = (state) => {
      if (state === SyncState.Prepared || state === SyncState.Syncing) {
        setIsLoading(false);
        updateUsers();
      }
    };

    // Initial state check
    const syncState = client.getSyncState();
    if (syncState === SyncState.Prepared || syncState === SyncState.Syncing) {
      setIsLoading(false);
      updateUsers();
    } else {
      setIsLoading(true);
    }

    // Listen for sync events to update loading state and user list
    client.on(ClientEvent.Sync, handleSync);
    // Also listen for new user events, which can happen when new members join rooms
    client.on(ClientEvent.User, updateUsers);

    return () => {
      client.off(ClientEvent.Sync, handleSync);
      client.off(ClientEvent.User, updateUsers);
    };
  }, [client]);

  // Fetch real avatars for all users
  useEffect(() => {
    if (!client || !users.length) {
      return;
    }

    const myUserId = client.getUserId();
    const contactUsers = users.filter(user => user.userId !== myUserId);

    let isMounted = true; // Guard against state updates after unmount

    // Fetch avatars for all contacts in parallel
    const fetchAvatars = async () => {
      // Filter out users whose avatars we already have to avoid redundant fetches
      // Use ref to get current state without adding to dependencies
      const usersNeedingAvatars = contactUsers.filter(
        user => !avatarsRef.current[user.userId],
      );

      if (usersNeedingAvatars.length === 0) {
        return; // All avatars already loaded
      }

      // Fetch all avatars in parallel for better performance
      const results = await Promise.all(
        usersNeedingAvatars.map(async (user) => {
          try {
            const avatarUrl = await getUserAvatar(
              user.userId,
              user.displayName || user.userId,
              40,  // Size for contact list
            );

            return { userId: user.userId, url: avatarUrl };
          } catch (error) {
            logger.warn(`Failed to fetch avatar for ${user.userId}:`, error);
            // Return null to keep the initials placeholder on error
            return null;
          }
        }),
      );

      // Batch update avatars state only if component is still mounted
      if (isMounted) {
        const newAvatars = {};
        results.forEach(result => {
          if (result && result.url) {
            newAvatars[result.userId] = result.url;
          }
        });

        if (Object.keys(newAvatars).length > 0) {
          setAvatars(prev => ({ ...prev, ...newAvatars }));
        }
      }
    };

    fetchAvatars();

    // Cleanup: only prevent state updates after unmount
    return () => {
      isMounted = false;
      // No cleanup needed - AvatarContext handles it automatically
    };
  }, [client, userIds, getUserAvatar]);
  // Note: 'users' is intentionally omitted. We only want to re-run when user IDs change
  // (additions/removals), not when user objects are updated. The userIds memo tracks this.

  // Listen for avatar changes and update in real-time
  // IMPORTANT: We listen to BOTH UserEvent.AvatarUrl AND RoomStateEvent.Members for defense in depth.
  // UserEvent.AvatarUrl often doesn't fire when users change avatars, but RoomStateEvent.Members
  // always fires because avatar changes come as m.room.member state events. Keeping both listeners
  // ensures we catch avatar updates through either path.
  useEffect(() => {
    if (!client) {
      return;
    }

    const handleAvatarChange = async (event, user) => {
      const changedUserId = user.userId;
      const myUserId = client.getUserId();

      // Ignore our own avatar changes in the contact list
      if (changedUserId === myUserId) {
        return;
      }

      logger.debug(`useContactList: Avatar changed for user ${changedUserId}`);

      try {
        // Invalidate the cached avatar via context
        await invalidateAvatar(changedUserId);

        // Fetch the new avatar
        const newAvatarUrl = await getUserAvatar(
          changedUserId,
          user.displayName || changedUserId,
          40,
        );

        // Update state with new avatar
        setAvatars(prev => ({
          ...prev,
          [changedUserId]: newAvatarUrl,
        }));

        logger.debug(`useContactList: Avatar updated for user ${changedUserId}`);
      } catch (error) {
        logger.warn(`useContactList: Failed to update avatar for ${changedUserId}:`, error);
        // On error, remove the avatar from state to fall back to initials
        setAvatars(prev => {
          const newAvatars = { ...prev };
          delete newAvatars[changedUserId];
          return newAvatars;
        });
      }
    };

    // Listen for RoomStateEvent.Members to catch avatar changes in m.room.member events
    // This is the PRIMARY mechanism - UserEvent.AvatarUrl often doesn't fire for avatar changes
    const handleMemberStateChange = async (event, _state, _member) => {
      // Only process m.room.member events
      if (event.getType() !== 'm.room.member') return;

      const content = event.getContent();
      const prevContent = event.getPrevContent();

      // Check if avatar_url changed
      if (content.avatar_url !== prevContent?.avatar_url) {
        const changedUserId = event.getStateKey();
        const myUserId = client.getUserId();

        // Ignore our own avatar changes in the contact list
        if (changedUserId === myUserId) {
          return;
        }

        logger.debug(`useContactList: RoomStateEvent.Members avatar change for user ${changedUserId}`);

        try {
          // Invalidate the cached avatar via context
          await invalidateAvatar(changedUserId);

          // Get display name from content or previous content
          const displayName = content.displayname || prevContent?.displayname || changedUserId;

          // Fetch the new avatar
          const newAvatarUrl = await getUserAvatar(
            changedUserId,
            displayName,
            40,
          );

          // Update state with new avatar
          setAvatars(prev => ({
            ...prev,
            [changedUserId]: newAvatarUrl,
          }));

          logger.debug(`useContactList: Avatar updated via RoomStateEvent for user ${changedUserId}`);
        } catch (error) {
          logger.warn(`useContactList: Failed to update avatar for ${changedUserId}:`, error);
          // On error, remove the avatar from state to fall back to initials
          setAvatars(prev => {
            const newAvatars = { ...prev };
            delete newAvatars[changedUserId];
            return newAvatars;
          });
        }
      }
    };

    client.on(UserEvent.AvatarUrl, handleAvatarChange);
    client.on(RoomStateEvent.Members, handleMemberStateChange);

    logger.debug('useContactList: Avatar change listeners registered');

    return () => {
      client.off(UserEvent.AvatarUrl, handleAvatarChange);
      client.off(RoomStateEvent.Members, handleMemberStateChange);
      logger.debug('useContactList: Avatar change listeners removed');
    };
  }, [client, invalidateAvatar, getUserAvatar]);

  const contactList = useMemo(() => {
    if (isLoading || !users.length || !client) {
      return [];
    }

    const myUserId = client.getUserId();

    return users
      .filter(user => user.userId !== myUserId)
      .map(user => {
        const contact = transformUserToContact(user);
        if (!contact) return null;

        // Replace initials with real avatar if available
        if (avatars[user.userId]) {
          contact.avatar = avatars[user.userId];
        }

        return contact;
      })
      .filter(Boolean);
  }, [users, isLoading, client, avatars]);

  return { contactList, isLoading };
};
